import { useEffect } from 'react';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import CategorySection from './components/CategorySection';
import HowToSection from './components/HowToSection';
import AboutSection from './components/AboutSection';
import FAQSection from './components/FAQSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://otake-buy.jp';

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "株式会社OTAKE",
  "url": siteUrl,
  "description": "岡山市中区の買取専門店。貴金属・ブランド品・家電・ホビー・家具を高価買取。査定無料・即日対応・出張買取OK。",
  "inLanguage": "ja",
  "publisher": {
    "@type": "Organization",
    "name": "株式会社OTAKE",
    "url": siteUrl
  }
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "Store"],
  "name": "株式会社OTAKE",
  "description": "岡山市中区を拠点とする買取専門会社。貴金属・ブランド品・家電デジタル製品・ホビー用品・家具生活用品などの高価買取を行っています。",
  "url": siteUrl,
  "telephone": "+81-86-238-7864",
  "faxNumber": "+81-86-012-3457",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "倉田652番地15",
    "addressLocality": "岡山市中区",
    "addressRegion": "岡山県",
    "postalCode": "703-8271",
    "addressCountry": "JP"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 34.6535,
    "longitude": 133.9700
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  ],
  "priceRange": "¥",
  "currenciesAccepted": "JPY",
  "paymentAccepted": "Cash, Bank Transfer",
  "areaServed": [
    { "@type": "City", "name": "岡山市" },
    { "@type": "City", "name": "倉敷市" },
    { "@type": "City", "name": "総社市" },
    { "@type": "City", "name": "玉野市" },
    { "@type": "City", "name": "赤磐市" }
  ],
  "founder": {
    "@type": "Person",
    "name": "永井 健男"
  },
  "foundingDate": "2025",
  "numberOfEmployees": {
    "@type": "QuantitativeValue",
    "value": 5
  },
  "hasCredential": ["古物商許可証"],
  "sameAs": []
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "査定は無料ですか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "はい、査定は完全無料です。査定後のキャンセルも一切費用はかかりません。まずはお気軽にご相談ください。"
      }
    },
    {
      "@type": "Question",
      "name": "岡山市以外からでも利用できますか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "出張買取は岡山市を中心に近隣市町村（倉敷市・総社市・玉野市・赤磐市など）に対応しています。宅配買取は全国対応可能です。"
      }
    },
    {
      "@type": "Question",
      "name": "どのような状態の品物でも買取可能ですか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "傷や汚れがある商品、動作しない家電なども査定いたします。状態によっては買取できない場合もありますが、まずはご相談ください。"
      }
    },
    {
      "@type": "Question",
      "name": "本人確認書類は必ず必要ですか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "古物営業法の規定により、買取の際は必ず本人確認が必要です。運転免許証・マイナンバーカード・健康保険証などをご用意ください。"
      }
    },
    {
      "@type": "Question",
      "name": "買取金額はいつ受け取れますか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "店頭買取・出張買取の場合は、ご成約当日に現金でお支払いいたします。宅配買取は商品到着後3〜5営業日以内に銀行振込にて対応いたします。"
      }
    },
    {
      "@type": "Question",
      "name": "複数の品物をまとめて査定できますか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "もちろんです。複数点をまとめてお持ちいただくと、まとめ買取割増になる場合もございます。引越し時の大量処分などもお気軽にご相談ください。"
      }
    },
    {
      "@type": "Question",
      "name": "出張買取は費用がかかりますか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "出張費・査定費は完全無料です。成約に至らなかった場合でも費用は一切いただきません。"
      }
    },
    {
      "@type": "Question",
      "name": "買取成立後のキャンセルは可能ですか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "原則として買取成立後のキャンセルはお受けしておりません。査定額にご納得いただいた上でご成約となりますので、ご不明点はご成約前にお気軽にお申し付けください。"
      }
    },
    {
      "@type": "Question",
      "name": "営業時間外の問い合わせは可能ですか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "ウェブサイトのお問い合わせフォームは24時間受付しております。翌営業日（9:00〜18:00）に担当スタッフよりご連絡いたします。"
      }
    },
    {
      "@type": "Question",
      "name": "宅配買取の梱包材はどうすれば良いですか？",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "段ボールや緩衝材はお客様にてご用意をお願いしております。まずはお問い合わせフォームよりお申込みください。送付先をご案内いたします。"
      }
    }
  ]
};

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Header />
      <main>
        <HeroSection />
        <CategorySection />
        <HowToSection />
        <AboutSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
